import { inject } from '@angular/core';
import { HttpEvent, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, of, tap, throwError } from 'rxjs';
import { catchError, delay, filter, map, switchMap, take } from 'rxjs/operators';
import { AuthService } from '@fluxonos/features/user/services/auth.service';
import { USER_CONST } from '@fluxonos/fluxonos-dto';

const isRefreshing$ = new BehaviorSubject<boolean>(false);
const allowWithoutToken = ['./', 'assets/'];

export function authInterceptor(request: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
  const authService = inject(AuthService);
  const router = inject(Router);

  const skipAuth = request.headers.get('skipAuth');
  const skipAuthRefresh = request.headers.get('skipAuthRefresh');

  request = request.clone({
    headers: request.headers.delete('skipAuth').delete('skipAuthRefresh')
  });

  if (allowWithoutToken.some((item) => request.url.includes(item)) || skipAuth === '1') {
    return next(request);
  }

  // Add auth token if available
  if (!request.headers.has(USER_CONST.AUTHORIZATION_HEADER) && authService.token) {
    const headers: { [header: string]: string } = {};
    headers[USER_CONST.AUTHORIZATION_HEADER] = `bearer ${authService.token.accessToken}`;
    request = request.clone({
      setHeaders: headers
    });
  }

  if (authService.token?.isAccessTokenExpired && skipAuthRefresh !== '1') {
    return refreshTokenAndRetry(request, next, skipAuthRefresh, authService, router);
  } else {
    return handleRequest(request, next, skipAuthRefresh, authService, router);
  }
}

function refreshTokenAndRetry(
  request: HttpRequest<unknown>,
  next: HttpHandlerFn,
  skipAuthRefresh: string | null,
  authService: AuthService,
  router: Router
): Observable<HttpEvent<any>> {
  if (isRefreshing$.value) {
    // If a refresh is already in progress, wait for it to complete
    return isRefreshing$.pipe(
      filter((value) => !value),
      delay(100),
      take(1),
      switchMap(() => {
        const headers: { [header: string]: string } = {};
        headers[USER_CONST.AUTHORIZATION_HEADER] = `bearer ${authService.token?.accessToken}`;
        request = request.clone({
          setHeaders: headers
        });
        return handleRequest(request, next, skipAuthRefresh, authService, router);
      })
    );
  } else {
    // Start a new refresh
    isRefreshing$.next(true);
    return authService.refreshToken(true, true, true).pipe(
      catchError((err) => {
        skipAuthRefresh = '1';
        return unauthLogout(err, authService, router);
      }),
      switchMap(() => {
        isRefreshing$.next(false);
        const headers: { [header: string]: string } = {};
        headers[USER_CONST.AUTHORIZATION_HEADER] = `bearer ${authService.token?.accessToken}`;
        request = request.clone({
          setHeaders: headers
        });
        return handleRequest(request, next, skipAuthRefresh, authService, router);
      })
    );
  }
}

function handleRequest(
  request: HttpRequest<unknown>,
  next: HttpHandlerFn,
  skipAuthRefresh: string | null,
  authService: AuthService,
  router: Router
): Observable<HttpEvent<unknown>> {
  return next(request).pipe(
    catchError((err) => {
      if (skipAuthRefresh === '1' || err.status !== 401) {
        return throwError(() => err);
      }
      return refreshTokenAndRetry(request, next, skipAuthRefresh, authService, router);
    })
  );
}

function unauthLogout(err: any, authService: AuthService, router: Router): Observable<boolean> {
  return of(true).pipe(
    switchMap(() => {
      return router.navigate(['/', 'user', 'login']);
    }),
    tap(() => {
      authService.cleanUp();
    }),
    map(() => {
      isRefreshing$.next(false);
      return true;
    })
  );
}
